import { useApiResource } from '~/composables/api/useApiResource'

export const useGanttApi = () => {
  const { loading, error, customEndpoint } = useApiResource('promotions', {
    deserialize: false,
    methods: {
      list: false,
      getById: false,
      create: false,
      update: false,
      delete: false
    }
  })

  const getGantt = customEndpoint((promotionId) => ({
    url: `/promotions/${promotionId}/gantt`,
    method: 'GET',
    deserialize: false
  }))

  /** Versión concreta del Gantt (histórica read-only); por defecto la activa (INV-G-1) */
  const getGanttVersion = customEndpoint((promotionId, ganttId) => ({
    url: `/promotions/${promotionId}/gantt?ganttId=${ganttId}`,
    method: 'GET',
    deserialize: false
  }))

  /** Versiones del Gantt de la promoción, de más reciente a más antigua */
  const listGanttVersions = customEndpoint((promotionId) => ({
    url: `/promotions/${promotionId}/gantt/versions`,
    method: 'GET',
    deserialize: false
  }))

  /** Edita el título de una versión; vacío → default por fecha (INV-G-1) */
  const updateGanttVersionTitle = customEndpoint((promotionId, ganttId, title) => ({
    url: `/promotions/${promotionId}/gantt/versions/${ganttId}`,
    method: 'PATCH',
    data: { title },
    deserialize: false
  }))

  /**
   * Notas de la planificación de la versión activa; guardado independiente del
   * borrador (botón propio en el panel). Vacío → `null`. Devuelve el gantt
   * completo (mismo shape que `GET .../gantt`).
   */
  const updateGanttNotes = customEndpoint((promotionId, notes) => ({
    url: `/promotions/${promotionId}/gantt/notes`,
    method: 'PATCH',
    data: { notes },
    deserialize: false
  }))

  const updateWeights = customEndpoint((promotionId, weights) => ({
    url: `/promotions/${promotionId}/gantt/weights`,
    method: 'PATCH',
    data: { weights },
    deserialize: false
  }))

  /** `dryRun: true` devuelve el preview de impacto sin persistir nada */
  const recalculate = customEndpoint((promotionId, options = {}) => ({
    url: `/promotions/${promotionId}/gantt/recalculations`,
    method: 'POST',
    data: { dryRun: false, force: false, reseedWeights: false, ...options },
    deserialize: false
  }))

  /**
   * Estado de planificación para avisos de "presupuesto fuera del Gantt"
   * (insignia del menú Planificación, INV-G-25). Respuesta plana `{ data }`.
   */
  const getPlanningStatus = customEndpoint((promotionId) => ({
    url: `/promotions/${promotionId}/planning-status`,
    method: 'GET',
    deserialize: false
  }))

  const updateGanttItem = customEndpoint((promotionId, budgetItemId, data) => ({
    url: `/promotions/${promotionId}/gantt/items/${budgetItemId}`,
    method: 'PATCH',
    data,
    deserialize: false
  }))

  /** Replace-set de las predecesoras de un item: `[{ predecessorId, lagDays?, title? }]` */
  const saveItemDependencies = customEndpoint((promotionId, budgetItemId, dependencies) => ({
    url: `/promotions/${promotionId}/gantt/items/${budgetItemId}/dependencies`,
    method: 'PUT',
    data: { dependencies },
    deserialize: false
  }))

  /**
   * Preview de replanificación asistida por IA. La respuesta no es JSON:API
   * estándar (el `data` no lleva `id`): se lee cruda y el consumidor usa
   * `data.attributes` (propuesta) y `meta.warnings`. `currentPlan` es el plan
   * que ve el navegador (borrador incluido): el LLM replanifica sobre él y la
   * respuesta se valida contra sus ids, no contra la DB.
   */
  const generateAiPlan = customEndpoint((promotionId, { instructions, currentPlan } = {}) => ({
    url: `/promotions/${promotionId}/gantt/ai-plan`,
    method: 'POST',
    data: {
      ...(instructions ? { instructions } : {}),
      ...(currentPlan ? { currentPlan } : {})
    },
    deserialize: false
  }))

  /** Materializa una propuesta de `generateAiPlan` (se reenvía íntegra) */
  const applyAiPlan = customEndpoint((promotionId, proposal) => ({
    url: `/promotions/${promotionId}/gantt/ai-plan/apply`,
    method: 'POST',
    data: proposal,
    deserialize: false
  }))

  /**
   * Calendario de obra [from, to] (YYYY-MM-DD): `{ ccaa, holidays }` (la API
   * puede seguir devolviendo `attribution`, pero la UI ya no lo pinta).
   * No es JSON:API (el `data` no lleva `id` por item): se lee crudo como el resto
   * de este composable (`response.data` directo).
   */
  const getCalendar = customEndpoint((promotionId, from, to) => ({
    url: `/promotions/${promotionId}/calendar?from=${from}&to=${to}`,
    method: 'GET',
    deserialize: false
  }))

  /** Alta de festivo/cierre de la obra (`409 promotion_holiday_duplicate`; scope default 'local') */
  const saveCalendarHoliday = customEndpoint((promotionId, { date, name, scope }) => ({
    url: `/promotions/${promotionId}/calendar/holidays`,
    method: 'POST',
    data: { date, name, ...(scope ? { scope } : {}) },
    deserialize: false
  }))

  /**
   * Edición de un festivo de la promoción (cualquiera: copia del global o
   * manual). ≥1 campo; colisión de fecha → `409 promotion_holiday_duplicate`.
   */
  const updateCalendarHoliday = customEndpoint((promotionId, holidayId, { date, name, scope } = {}) => ({
    url: `/promotions/${promotionId}/calendar/holidays/${holidayId}`,
    method: 'PATCH',
    data: { date, name, scope },
    deserialize: false
  }))

  /** Copia no destructiva de los festivos del calendario global que faltan en la obra */
  const importCalendarHolidays = customEndpoint((promotionId) => ({
    url: `/promotions/${promotionId}/calendar/import`,
    method: 'POST',
    deserialize: false
  }))

  /** Baja de un festivo de la obra (copia o manual); los globales no se tocan */
  const deleteCalendarHoliday = customEndpoint((promotionId, holidayId) => ({
    url: `/promotions/${promotionId}/calendar/holidays/${holidayId}`,
    method: 'DELETE',
    deserialize: false
  }))

  return {
    loading,
    error,
    getGantt,
    getGanttVersion,
    listGanttVersions,
    updateGanttVersionTitle,
    updateGanttNotes,
    updateWeights,
    recalculate,
    getPlanningStatus,
    updateGanttItem,
    saveItemDependencies,
    generateAiPlan,
    applyAiPlan,
    getCalendar,
    saveCalendarHoliday,
    updateCalendarHoliday,
    importCalendarHolidays,
    deleteCalendarHoliday
  }
}
